/**
 * Where jflo's synced data comes from, and how old each source's data may get before a page says
 * it's stale (decision 35 in docs/ui-api-consistency-audit.md). The one list of thresholds: the
 * DataFreshness chip, the Integrations panels, the Sync tab's overdue notice and the admins'
 * overdue email all read it. How pages use it: .cursor/rules/data-freshness.mdc.
 */

/**
 * Every source syncs once a night (Shopify and Xero in the main sync at 02:00 UTC, marketing
 * straight after). A day and a half allows for a slow or late run before the data counts as stale.
 */
export const NIGHTLY_SYNC_STALE_AFTER_HOURS = 36;

export const DATA_SOURCE_IDS = [
  'shopify',
  'xero',
  'google_ads',
  'ga4',
  'search_console',
  'microsoft_ads',
  'meta_ads',
  'instagram',
  'facebook',
  'ahrefs',
  'search_volumes',
] as const;

export type DataSourceId = (typeof DATA_SOURCE_IDS)[number];

export interface DataSourceInfo {
  /** The name a person knows it by. */
  label: string;
  /** Older than this since its last sync, the source's data is stale (amber). */
  staleAfterHours: number;
  /** Where it is connected and synced. */
  integrationsPath: string;
}

export const DATA_SOURCES: Record<DataSourceId, DataSourceInfo> = {
  shopify: {
    label: 'Shopify',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=shopify',
  },
  xero: {
    label: 'Xero',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=xero',
  },
  google_ads: {
    label: 'Google Ads',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=google',
  },
  ga4: {
    label: 'Google Analytics',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=google',
  },
  search_console: {
    label: 'Search Console',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=google',
  },
  microsoft_ads: {
    label: 'Microsoft Ads',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=microsoft',
  },
  meta_ads: {
    label: 'Meta Ads',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=meta',
  },
  instagram: {
    label: 'Instagram',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=instagram',
  },
  facebook: {
    label: 'Facebook',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=facebook',
  },
  ahrefs: {
    label: 'Ahrefs',
    staleAfterHours: NIGHTLY_SYNC_STALE_AFTER_HOURS,
    integrationsPath: '/integrations?tab=ahrefs',
  },
  // Google Ads' Keyword Planner (T-424): each term's figure is fetched again after 30 days
  // (SEARCH_VOLUME_REFRESH_DAYS), so the newest is never more than a month and a day or two old.
  search_volumes: {
    label: 'Google search volumes',
    staleAfterHours: 32 * 24,
    integrationsPath: '/integrations?tab=google',
  },
};

export function isDataSourceId(value: unknown): value is DataSourceId {
  return typeof value === 'string' && (DATA_SOURCE_IDS as readonly string[]).includes(value);
}

/**
 * True when data last synced at `lastSyncedAt` is older than `staleAfterHours` at `now`. Never
 * synced (null, or a value that doesn't parse) counts as stale.
 */
export function isPastStaleAfter(
  lastSyncedAt: string | null | undefined,
  staleAfterHours: number,
  now: number
): boolean {
  if (!lastSyncedAt) return true;
  const at = Date.parse(lastSyncedAt);
  if (!Number.isFinite(at)) return true;
  return now - at >= staleAfterHours * 60 * 60 * 1000;
}
